import { useEffect, useId, useRef, useState, useSyncExternalStore, type FocusEvent, type ReactElement } from "react";
import { BODY_TYPE, pixelType } from "odyssey-shared";
import { Button, ConfirmDialog, Dialog, KIT_METRICS, List, ModalWindow, TextLink, centreOffset, logical, useScrollCue, type ListEntry } from "../../ui/kit/index.ts";
import { pageKeyboard } from "../controls/keyboard-use.ts";
import { ControlsPane, type ControlsEvents } from "./controls-pane.tsx";
import { applyDraft, changedCount, discardDraft, openDraft, rebaseValue, resetKey, resetSection, sectionAtDefaults, sectionSettings, setKey, setLevel, setValue, type SettingsDraft, type SettingsSection } from "./settings-model.ts";
import { applySettings, previewLevels, readSettings, restoreLevels, revertLevels, type SettingsPage } from "./settings-page.ts";
import { renderCost } from "./render-choices.ts";
import { SettingsPane, type FrameWindow, type PaneEvents, type RenderWindow } from "./settings-panes.tsx";
import { SETTINGS_WINDOW, settingsWindowBox, shownRows, shownSections } from "./settings-sections.ts";
import { resetWindows, windowPlace, windowSize } from "../windows/places.ts";
import { changesNote, resetQuestion, unsavedSentence, unsavedWidth } from "./wording.ts";
import { say } from "../../words.ts";
import { TOUCH_QUERY } from "../touch/page.ts";

/**
 * Settings (P1.9-T44; ADR-061's amendment "Settings in sections, and nothing applies until Apply"; the design's
 * `Settings`, a visual reference): the kit's modal window at the design's 612 by 236 until it is moved or resized
 * (P1.9-T184: by its title bar, edges and grip, from 420 by 200 to 900 by 520, kept per device under `settings`; the
 * dialogs it asks stay centred and fixed), never past the page, with
 * the section list at the left (a listbox, 96 wide, scrolling inside the window), a rule, and the chosen
 * section's pane of rows, which scrolls. Its footer holds Reset this tab at the left and, at the right, how many
 * rows are changed, Cancel and Apply.
 *
 * It opens a draft on the page's values in force. A row's change is pending and marked; only a level and its
 * Mute are heard at once. Apply puts the pending values in force, keeps them and stays. Cancel, the close button
 * and Escape leave for the menu when nothing is pending, and otherwise ask "Apply your changes?": Apply applies
 * and leaves, Discard drops the changes, returns the levels heard and leaves, and Escape there returns to
 * Settings with the changes kept. Reset this tab asks first, then sets the shown section's rows to their defaults
 * as pending changes, and is disabled while they already are. If Settings leaves the page with changes pending
 * (the page left the world), they are dropped and every level is returned to the one in force.
 *
 * Render resolution (ADR-093 decision 10) is weighed again while Settings is open: a resize, browser zoom or a
 * move to another screen changes what the window offers, and when the divisor in force falls back the draft
 * follows it, a pending choice the window still offers staying pending. Where applying would show less of the
 * world or a smaller HUD, the row's note and the question on closing both say so.
 *
 * Controls (P1.9-T50, `controls-pane.tsx`) lists each action's key; a key chosen there is a pending change like any
 * other, a clash swaps on Apply, and the whole table is put in force at once. On a touch page the section is left
 * out until a keyboard is in use (`controls/keyboard-use.ts`; the author's ruling of 2026-10-07: "hidden from mobile,
 * unless a bluetooth/keyboard is plugged in."), and it joins the list at the first hardware key press.
 *
 * A control that takes focus in a row the pane cuts off is scrolled whole into view, to the row top that shows
 * it, since the pane rests only on row tops.
 */

/**
 * What Settings is asking, over its window: whether to apply pending changes before it closes, whether to reset the
 * shown section, or whether to reset the ability bar (ADR-121 decision 2.5); null while it asks nothing.
 */
type Asking = "close" | "reset" | "barReset" | null;

/**
 * The dialog asked when Settings closes with changes pending. It opens with focus on Apply, as a confirm dialog
 * opens on its confirm button, so Enter keeps the changes; Escape returns to Settings.
 *
 * @param props How many rows are changed (`count`), what applying would cost the viewer or null (`cost`), and
 *   `onApply`, `onDiscard` and `onBack`.
 * @returns The dialog.
 */
function UnsavedDialog({ count, cost, onApply, onDiscard, onBack }: { readonly count: number; readonly cost: string | null; readonly onApply: () => void; readonly onDiscard: () => void; readonly onBack: () => void }): ReactElement {
  const bodyId = useId();
  useEffect(() => {
    document.getElementById(bodyId)?.closest("[data-kit='dialog']")?.querySelector<HTMLElement>("[data-kit='dialog-actions'] button:last-of-type")?.focus({ focusVisible: true });
  }, [bodyId]);
  return (
    <Dialog
      title={say("settings.unsavedTitle")}
      width={unsavedWidth()}
      onClose={onBack}
      describedBy={bodyId}
      actions={
        <>
          <Button onClick={onDiscard}>{say("settings.discard")}</Button>
          <Button variant="primary" onClick={onApply}>
            {say("settings.apply")}
          </Button>
        </>
      }
    >
      <div id={bodyId} data-game-menu="unsaved">
        {unsavedSentence(count, cost)}
      </div>
    </Dialog>
  );
}

/**
 * The footer's content: Reset this tab at the left, for a section with a setting to reset, disabled while the
 * section's rows are already at their defaults; and at the right, 3 apart as the design draws them, the count of
 * changed rows, Cancel and Apply, which is disabled while nothing is changed. The count is a polite live region
 * that stays on the page, empty while nothing is changed, since assistive technology reads a sentence that
 * appears in a region already there. The link and the count sit in the middle of the buttons' height.
 *
 * @param props Whether the section has a setting to reset (`resets`) and whether it is already at its defaults
 *   (`atDefaults`), the count of changed rows, and `onReset`, `onCancel` and `onApply`.
 * @returns The footer's content.
 */
function Footer({ resets, atDefaults, count, onReset, onCancel, onApply }: { readonly resets: boolean; readonly atDefaults: boolean; readonly count: number; readonly onReset: () => void; readonly onCancel: () => void; readonly onApply: () => void }): ReactElement {
  const button = KIT_METRICS.button.height;
  return (
    <>
      <div className="ot-game-settings__reset" data-game-menu="reset" style={{ marginTop: logical(centreOffset(button, pixelType("label").lineHeight)) }}>
        {resets ? (
          <TextLink disabled={atDefaults} onClick={onReset}>
            {say("settings.reset")}
          </TextLink>
        ) : null}
      </div>
      <div className="ot-game-settings__actions" data-game-menu="actions" style={{ gap: logical(SETTINGS_WINDOW.footerGap) }}>
        <span className="ot-game-settings__count ot-centred__text" data-game-menu="changes" aria-live="polite" style={{ marginTop: logical(centreOffset(button, BODY_TYPE.body.lineHeight)) }}>
          {count === 0 ? null : changesNote(count)}
        </span>
        <Button onClick={onCancel}>{say("settings.cancel")}</Button>
        <Button variant="primary" disabled={count === 0} onClick={onApply}>
          {say("settings.apply")}
        </Button>
      </div>
    </>
  );
}

/**
 * Whether the page is a touch page now, following the device as a mouse or a keyboard cover comes and goes. Where
 * there is no window to ask, it is not.
 *
 * @returns True on a touch page.
 */
function useTouchPage(): boolean {
  const [touch, setTouch] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(TOUCH_QUERY).matches);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const query = window.matchMedia(TOUCH_QUERY);
    /**
     * Takes the query's answer.
     */
    const follow = (): void => setTouch(query.matches);
    follow();
    query.addEventListener("change", follow);
    return () => query.removeEventListener("change", follow);
  }, []);
  return touch;
}

/**
 * Scrolls the pane so the row holding the control that took focus shows whole. The pane rests only on row tops
 * (its scroll snaps to them), so a row cut off at the bottom is shown by scrolling to the first row top that
 * brings its bottom inside, and one cut off at the top by scrolling to its own top. A focus a pointer press gave
 * scrolls nothing: moving the pane between the press and the release would land the release on another control
 * and drop the click.
 *
 * @param event The focus event inside the pane.
 * @param pressed Whether a pointer press inside the pane is held.
 */
function revealRow(event: FocusEvent<HTMLDivElement>, pressed: boolean): void {
  if (pressed) {
    return;
  }
  const pane = event.currentTarget;
  const row = event.target instanceof Element ? event.target.closest<HTMLElement>(".ot-game-settings__row, .ot-game-settings__action") : null;
  const first = pane.firstElementChild;
  if (row === null || !(first instanceof HTMLElement)) {
    return;
  }
  const view = pane.getBoundingClientRect();
  const box = row.getBoundingClientRect();
  if (box.top < view.top) {
    pane.scrollTop = row.offsetTop - first.offsetTop;
  } else if (box.bottom > view.bottom) {
    const least = pane.scrollTop + box.bottom - view.bottom;
    const tops = [...pane.children].map((child) => (child as HTMLElement).offsetTop - first.offsetTop);
    pane.scrollTop = tops.find((top) => top >= least - 0.5) ?? pane.scrollHeight;
  }
}

/**
 * Settings.
 *
 * @param props The `page`'s settings; the Account section's button state, or null for a session with no Account
 *   section; `onEverywhere`, run by that button; and `onBack`, called when Settings leaves for the menu.
 * @returns The window, and over it the dialog it is asking, if any.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function SettingsWindow({ page, account, onEverywhere, onBack }: { readonly page: SettingsPage; readonly account: { readonly busy: boolean; readonly disabled: boolean } | null; readonly onEverywhere: () => void; readonly onBack: () => void }): ReactElement {
  const [draft, setDraft] = useState<SettingsDraft>(() => openDraft(readSettings(page)));
  const touch = useTouchPage();
  const keyboard = useSyncExternalStore(pageKeyboard.subscribe, pageKeyboard.getSnapshot, pageKeyboard.getSnapshot);
  const sections = shownSections({ account: account !== null, controls: !touch || keyboard, social: page.partyInvites !== undefined });
  const [chosen, setChosen] = useState<SettingsSection>(sections[0] as SettingsSection);
  const [asking, setAsking] = useState<Asking>(null);
  const section = sections.includes(chosen) ? chosen : (sections[0] as SettingsSection);
  const paneCue = useScrollCue();
  const latest = useRef({ page, draft });
  const pressed = useRef(false);
  const count = changedCount(draft);
  const render = page.render;
  const frameLimit = page.frameLimit;
  const [, setWeighed] = useState(0);
  const window: RenderWindow | null = render === undefined ? null : { device: render.device, offered: render.offered, scaleAt: (step) => render.scaleAt(step), mostScale: render.mostScale };
  const frames: FrameWindow | null = frameLimit === undefined ? null : { refresh: frameLimit.refresh };
  const cost = window === null ? null : renderCost(window, draft.applied.renderDivisor, draft.pending.renderDivisor, draft.pending.zoom);

  useEffect(
    () =>
      render?.subscribe(() => {
        setDraft((current) => rebaseValue(current, "renderDivisor", render.divisor, render.offered.includes(current.pending.renderDivisor)));
        setWeighed((times) => times + 1);
      }),
    [render],
  );

  useEffect(() => frameLimit?.subscribe(() => setWeighed((times) => times + 1)), [frameLimit]);

  useEffect(() => {
    latest.current = { page, draft };
  }, [page, draft]);

  useEffect(
    () => () => {
      restoreLevels(latest.current.page, latest.current.draft.applied);
    },
    [],
  );

  /**
   * Leaves for the menu when nothing is pending, and otherwise asks whether to apply.
   */
  const close = (): void => {
    if (count === 0) {
      onBack();
    } else {
      setAsking("close");
    }
  };

  /**
   * Puts the pending values in force, keeps them and clears the marks.
   */
  const apply = (): void => {
    applySettings(page, draft);
    const applied = applyDraft(draft);
    latest.current = { page, draft: applied };
    setDraft(applied);
  };

  /**
   * Drops the pending values, returning the levels heard to the ones in force.
   */
  const discard = (): void => {
    revertLevels(page, draft);
    const dropped = discardDraft(draft);
    latest.current = { page, draft: dropped };
    setDraft(dropped);
  };

  /**
   * Sets the shown section's rows to their defaults as pending changes; the levels a reset moves are heard.
   */
  const reset = (): void => {
    const next = resetSection(draft, section);
    previewLevels(page, draft.pending, next.pending);
    setDraft(next);
    setAsking(null);
  };

  const events: PaneEvents = {
    /**
     * Takes a plain setting's pending value.
     *
     * @param id The setting.
     * @param value The value.
     */
    onValue: (id, value) => setDraft((current) => setValue(current, id, value)),
    /**
     * Takes a level's pending value and lets it be heard.
     *
     * @param id Master or a channel.
     * @param level The level.
     */
    onLevel: (id, level) => {
      page.audio.previewLevel(id, level);
      setDraft((current) => setLevel(current, id, level));
    },
    onEverywhere,
    onResetWindows: () => resetWindows(),
  };

  const controls: ControlsEvents = {
    /**
     * Takes a key chosen for an action as a pending change.
     *
     * @param action The action.
     * @param key The key.
     */
    onKey: (action, key) => setDraft((current) => setKey(current, action, key)),
    /**
     * Sets one action's pending key to its default.
     *
     * @param action The action.
     */
    onResetKey: (action) => setDraft((current) => resetKey(current, action)),
    /**
     * Takes Run's mode as a pending change.
     *
     * @param mode Hold or Toggle.
     */
    onRunMode: (mode) => setDraft((current) => setValue(current, "runMode", mode)),
    onLayout: (choice) => setDraft((current) => setValue(current, "keyLayout", choice)),
    /**
     * Takes Lock the ability bar as a pending change.
     *
     * @param locked Whether the bar is locked.
     */
    onBarLocked: page.barLock === undefined ? undefined : (locked) => setDraft((current) => setValue(current, "barLocked", locked)),
    onBarReset: page.barReset?.canReset() === true ? () => setAsking("barReset") : undefined,
  };

  const rows = shownRows(section, { performance: page.performance !== undefined, render: window !== null, frameLimit: frameLimit !== undefined, keys: !touch || keyboard });
  const items: readonly ListEntry<SettingsSection>[] = sections.map((value) => ({ value, label: say(`settings.sections.${value}`) }));
  const question = resetQuestion(section);
  return (
    <>
      <ModalWindow id="settings" place={windowPlace("settings")} size={windowSize("settings")} title={say("settings.title")} onClose={close} fit={settingsWindowBox} footer={<Footer resets={sectionSettings(section).length > 0} atDefaults={sectionAtDefaults(draft, section)} count={count} onReset={() => setAsking("reset")} onCancel={close} onApply={apply} />}>
        <div className="ot-game-settings" data-game-menu="settings" style={{ gap: logical(SETTINGS_WINDOW.gap) }}>
          <div className="ot-game-settings__sections" data-game-menu="sections">
            <List label={say("settings.sectionList")} items={items} value={section} onChange={setChosen} empty={null} width={SETTINGS_WINDOW.sections} />
          </div>
          <div className="ot-game-settings__rule" style={{ width: logical(SETTINGS_WINDOW.rule) }} />
          <div ref={paneCue} className="ot-game-settings__pane ot-scroll" role="group" aria-label={say(`settings.sections.${section}`)} data-game-menu="pane" data-section={section} onPointerDownCapture={() => (pressed.current = true)} onPointerUpCapture={() => (pressed.current = false)} onPointerCancelCapture={() => (pressed.current = false)} onKeyDownCapture={() => (pressed.current = false)} onFocus={(event) => revealRow(event, pressed.current)} style={{ gap: logical(SETTINGS_WINDOW.rowGap), paddingRight: logical(SETTINGS_WINDOW.scrollGap) }}>
            {section === "controls" ? <ControlsPane rows={rows} draft={draft} events={controls} /> : <SettingsPane section={section} rows={rows} draft={draft} account={account} window={window} frames={frames} events={events} />}
          </div>
        </div>
      </ModalWindow>
      {asking === "close" ? (
        <UnsavedDialog
          count={count}
          cost={cost}
          onBack={() => setAsking(null)}
          onApply={() => {
            apply();
            setAsking(null);
            onBack();
          }}
          onDiscard={() => {
            discard();
            setAsking(null);
            onBack();
          }}
        />
      ) : null}
      {asking === "barReset" ? (
        <ConfirmDialog
          title={say("controls.barReset")}
          confirmLabel={say("controls.barResetButton")}
          onConfirm={() => {
            page.barReset?.reset();
            setAsking(null);
          }}
          onCancel={() => setAsking(null)}
        >
          {say("controls.barResetBody")}
        </ConfirmDialog>
      ) : null}
      {asking === "reset" ? (
        <ConfirmDialog title={question.title} confirmLabel={say("settings.resetConfirm")} onConfirm={reset} onCancel={() => setAsking(null)}>
          {question.body}
        </ConfirmDialog>
      ) : null}
    </>
  );
}
